import React from 'react';
import {Stack, Typography} from '@mui/material';

import {ScrollToTop} from 'presentation/components';
import {BookingErrorState} from 'presentation/modules/booking/shared/ui/components/BookingErrorState';
import {BookingLoadingState} from 'presentation/modules/booking/shared/ui/components/BookingLoadingState';
import {translations} from 'presentation/translations-new';
import {HealthUnitsProps} from './types';
import {SpecialtyNotAvailableOnlineCard} from 'presentation/modules/booking/shared/ui/components/SpecialtyNotAvailableOnlineCard';
import {RequestSchedulePermissionCard} from 'presentation/modules/booking/shared/ui/components/RequestSchedulePermissionCard';
import {HealthUnitsNotFoundState} from './components/HealthUnitsNotFoundState';
import {HealthUnitsSelfNetwork} from './components/HealthUnitsSelfNetwork';
import {HealthUnitsAccreditedNetwork} from './components/HealthUnitsAccreditedNetwork';
import {useHealthUnits} from './hooks/useHealthUnits';
import {TelehealthSection} from './components/TelehealthSection';

const {loadingError, loadingState} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow;
const {bookHealth} = translations['pt-br'].privateFlow.services;
const {resultFound} = translations['pt-br'].commons;

export function HealthUnits({handleNextStep}: HealthUnitsProps) {
	const {
		specialtyNotAvailableOnline,
		showLoadingState,
		handleRefetchUnits,
		showErrorState,
		allowsRequestScheduleRegistration,
		healthRequestSchedulePermissionState,
		showNotFoundState,
		query,
		showSelfNetwork,
		showAccreditedNetwork,
		showEmptyAppointmentsCard,
		showSpecialtyNotAvailableInSelfNetwork,
		shouldReverseNetworkOrder,
		showTelehealthSection,
		telemedicineUnit,
		unitsList,
	} = useHealthUnits();

	if (showLoadingState) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={bookHealth}
				loadingText={loadingState}
				variant="skeleton"
			/>
		);
	}

	if (showErrorState) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={bookHealth}
				errorTitle={loadingError}
				handleRefresh={handleRefetchUnits}
			/>
		);
	}

	if (specialtyNotAvailableOnline) {
		return <SpecialtyNotAvailableOnlineCard />;
	}

	if (allowsRequestScheduleRegistration) {
		return (
			<RequestSchedulePermissionCard
				healthRequestSchedulePermission={healthRequestSchedulePermissionState}
			/>
		);
	}

	if (showNotFoundState) {
		return <HealthUnitsNotFoundState />;
	}

	return (
		<Stack gap={2}>
			{query ? (
				<Typography variant="subtitle2" color="text.secondary" sx={{mb: -1}}>
					{resultFound}
				</Typography>
			) : null}

			{showTelehealthSection ? <TelehealthSection unitCode={telemedicineUnit!.code} /> : null}

			<Stack flexDirection={shouldReverseNetworkOrder ? 'column-reverse' : 'column'}>
				{showSelfNetwork ? (
					<HealthUnitsSelfNetwork
						handleNextStep={handleNextStep}
						unitsList={unitsList}
						showEmptyAppointmentsCard={showEmptyAppointmentsCard}
						showSpecialtyNotAvailableCard={showSpecialtyNotAvailableInSelfNetwork}
					/>
				) : null}

				{showAccreditedNetwork ? (
					<HealthUnitsAccreditedNetwork
						unitsList={unitsList.units.unitsSupportAccredited}
					/>
				) : null}
			</Stack>

			<ScrollToTop />
		</Stack>
	);
}
